import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import styled, { css, StyleSheetManager } from "styled-components";
import { useCurrentProfile, useRegisterState } from "@domain/currentProfile/hooks";
import { InitializationState } from "@domain/currentProfile/initializationState";
import { RegisterState } from "@domain/currentProfile/registerState";
import { ScrollToTop } from "@ui/components/navigation/scrollToTop";
import { TopBar } from "@ui/components/navigation/topBar";
import { ModalContainer } from "@ui/modals/modalContainer";
import { ApplicationLandingPage } from "@ui/pages/application/applicationEditorPage/landingPage/applicationLandingPage";
import { AssetManagerPage } from "@ui/pages/assetManager/assetManagerPage";
import { BackofficePage } from "@ui/pages/backoffice/backofficePage";
import { LinkNumberToAccountPage } from "@ui/pages/firebase/firebaseActionPage";
import { HomePage } from "@ui/pages/homePage/homePage";
import { TopBanner } from "@ui/components/navigation/topBanner";
import { CguPage } from "@ui/pages/cgu/cguPage";
import { AuthPage } from "@ui/pages/loginPage/authPage";
import { NewOrganizationPage } from "@ui/pages/newOrganizationPage/newOrganizationPage";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { MyProfileRedirect } from "@ui/pages/profilePage/myProfileRedirect";
import { SettingsPage } from "@ui/pages/settingsPage/settingsPage";
import { TooshareToastContainer } from "@ui/toasts/tooshareToastContainer";
import { useLanguageFromProfile } from "@utils/hooks";
import { useServices } from "@core/servicesContext";
import { BlockLearnerCreation, RequireAuth } from "./authRoute";
import { Path } from "./path";
import {
	ApplicationEditorRoute,
	ApplicationSheetRoute,
	ArticleByIdRoute,
	ArticleBySlugRoute,
	ArticleEditorRoute,
	CarouselByIdRoute,
	CarouselBySlugRoute,
	ChatmessageRoute,
	CourseByIdRoute,
	CourseBySlugRoute,
	CourseContentRoute,
	CourseEditorRoute,
	DiscussionRoute,
	FirebaseActionRoute,
	GroupEditorRoute,
	LiveGroupEditorRoute,
	LiveGroupInfosRoute,
	LiveOrganizationEditorRoute,
	LiveOrganizationInfosRoute,
	OrganizationRoute,
	PostRoute,
	ProfileRoute,
	QuizEditorRoute,
	QuizRoute,
	SearchRoute,
	ShareroomRoute,
} from "./routeElements";
import isPropValid from "@emotion/is-prop-valid";
import { DebugPage } from "@ui/pages/debug/debugPage";
import { config } from "@core/config/config";
import { isDevEnvironment } from "@core/config/environment";

// import { useConnectUser } from "../../utils/messaging/hooks";
// import { MessagingControllerContext } from "../../domain/messaging/messagingControllerContext";

export const AppRouter: React.FC = () => {
	const registerState = useRegisterState();
	const { currentProfileService, trackingService } = useServices();
	const location = useLocation();
	const browsing =
		registerState === RegisterState.FINISHED ||
		(location.pathname !== Path.Root && !location.pathname.startsWith(Path.FirebaseAction));

	const isBannerOnTop = location.pathname === Path.Root && config.features.banner;
	const currentProfile = useCurrentProfile();
	useLanguageFromProfile(currentProfile?.language);

	useEffect(() => {
		currentProfileService.initialization.update((current) =>
			current === "IDLE" ? InitializationState.PENDING : current
		);
	}, []);

	useEffect(() => {
		//trackingService.track("page", { url: location.pathname });
		trackingService.logPageView(location.pathname);
	}, [location.pathname]);

	return (
		<StyleSheetManager
			enableVendorPrefixes
			shouldForwardProp={(propName, elementToBeRendered) => {
				return typeof elementToBeRendered === "string" ? isPropValid(propName) : true;
			}}
		>
			<ModalContainer>
				{isBannerOnTop && browsing ? <TopBanner /> : null}
				{browsing ? <TopBar isBannerOnTop={isBannerOnTop} /> : null}
				<AppContent browsing={browsing}>
					{/* <HelmetInfos title={I18n.t("app_name")} ogTitle={I18n.t("og_title")} /> */}
					<ScrollToTop />
					<Routes>
						<Route path="/articles/:id" element={<ArticleByIdRoute />} />
						<Route path="/articles/:category/:slug" element={<ArticleBySlugRoute />} />
						<Route path="/carousels/:id" element={<CarouselByIdRoute />} />
						<Route path="/carousels/:category/:slug" element={<CarouselBySlugRoute />} />
						<Route
							path="/article/edit/:id"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<ArticleEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route path="/profile/:profileKey/:tab?" element={<ProfileRoute />} />
						<Route path="/group/:groupKey/:tab?" element={<DiscussionRoute />} />
						<Route path="/shareroom/:roomKey/:tab?/:workspaceId?" element={<ShareroomRoute />} />
						<Route
							path="/groups/edit/:type/:id"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<GroupEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route
							path="/program/:type/apply/edit/:id"
							element={
								<RequireAuth>
									<ApplicationEditorRoute />
								</RequireAuth>
							}
						/>
						<Route
							path={Path.MyProfile}
							element={
								<RequireAuth>
									<MyProfileRedirect />
								</RequireAuth>
							}
						/>
						<Route path={Path.ApplicationLP} element={<ApplicationLandingPage />} />
						<Route
							path="/Chatmessage/:profileId"
							element={
								<RequireAuth>
									<ChatmessageRoute />
								</RequireAuth>
							}
						/>
						<Route path="/program/omat/view/application/:applicationKey" element={<ApplicationSheetRoute />} />
						<Route
							path="/lms/edit/:id"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<CourseEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route path="/lms/courses/:id" element={<CourseByIdRoute />} />
						<Route path="/courses/:language/:category?/:subCategory?/:slug" element={<CourseBySlugRoute />} />
						<Route
							path="/lms/courses/:id/content"
							element={
								<RequireAuth>
									<CourseContentRoute />
								</RequireAuth>
							}
						/>
						<Route
							path="/quiz/edit/:id"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<QuizEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route
							path={Path.Search}
							element={
								<RequireAuth>
									<SearchRoute />
								</RequireAuth>
							}
						/>
						<Route path="/post/:id" element={<PostRoute />} />
						<Route
							path="/quiz/:id"
							element={
								<RequireAuth>
									<QuizRoute />
								</RequireAuth>
							}
						/>
						<Route
							path="/quiz/:id/:source"
							element={
								<RequireAuth>
									<QuizRoute />
								</RequireAuth>
							}
						/>
						<Route
							path={Path.NewOrganization}
							element={
								<RequireAuth>
									<NewOrganizationPage />
								</RequireAuth>
							}
						/>
						<Route path="/organization/:orgKey/:tab?" element={<OrganizationRoute />} />
						<Route
							path={Path.Root}
							element={
								<RequireAuth guest={<AuthPage />}>
									<HomePage />
								</RequireAuth>
							}
						/>
						<Route
							path={Path.Settings}
							element={
								<RequireAuth guest={<AuthPage />}>
									<SettingsPage />
								</RequireAuth>
							}
						/>
						<Route
							path={Path.AssetManager}
							element={
								<RequireAuth>
									<AssetManagerPage />
								</RequireAuth>
							}
						/>
						<Route path={Path.FirebaseAction} element={<FirebaseActionRoute />} />
						<Route path={Path.LinkNumberAction} element={<LinkNumberToAccountPage />} />
						<Route
							path={Path.Backoffice}
							element={
								<RequireAuth admin guest={<AuthPage />}>
									<BackofficePage />
								</RequireAuth>
							}
						/>
						<Route
							path="/livecourses/Group/edit/:type/:shareroomId/:liveId"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<LiveGroupEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route
							path="/livecourses/Organization/edit/:type/:organizationId/:liveId"
							element={
								<RequireAuth>
									<BlockLearnerCreation>
										<LiveOrganizationEditorRoute />
									</BlockLearnerCreation>
								</RequireAuth>
							}
						/>
						<Route
							path="/livecourses/Group/:livecourseId/:shareroomId"
							element={
								<RequireAuth>
									<LiveGroupInfosRoute />
								</RequireAuth>
							}
						/>
						<Route
							path="/livecourses/Organization/:livecourseId/:organizationId"
							element={
								<RequireAuth>
									<LiveOrganizationInfosRoute />
								</RequireAuth>
							}
						/>
						<Route path={Path.Cgu} element={<CguPage />} />
						{isDevEnvironment ? <Route path={Path.Debug} element={<DebugPage />} /> : null}
						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</AppContent>
			</ModalContainer>
			<TooshareToastContainer />
		</StyleSheetManager>
	);
};

const AppContent = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "browsing",
})<{ browsing: boolean }>`
	flex: 1;
	display: flex;
	width: 100%;
	${({ browsing }) =>
		browsing
			? css`
					justify-content: center;
				`
			: css`
					flex: 1;
					& > * {
						width: 100%;
						flex: 1;
						min-width: 0;
					}
				`};
`;
